import axios from 'axios';

// Helper to get environment variable from runtime config or build-time env
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  // @ts-ignore - window.env is injected at runtime via env-config.js
  if (typeof window !== 'undefined' && window.env && window.env[key]) {
    // @ts-ignore
    return window.env[key] || defaultValue;
  }
  return (process.env[key] as string) || defaultValue;
};

// Create axios instance following OpenAPI specification
// Use relative path for production (proxied via Ingress)
const API_BASE_URL = getEnvVar('REACT_APP_API_BASE_URL', '');

const api = axios.create({
    baseURL: API_BASE_URL ? `${API_BASE_URL}/api/v1` : '/api/v1',
    timeout: 10000,
});

// Note: Axios interceptors for Keycloak authentication are set up in App.tsx
// using setupAxiosKeycloak() after Keycloak initialization

// Station service following OpenAPI specification
export const stationService = {
    // GET /api/v1/stations/ - List stations
    getAll: async (params?: {
        skip?: number;
        limit?: number;
        include_public?: boolean;
    }) => {
        try {
            const queryParams = new URLSearchParams();
            if (params?.skip !== undefined) queryParams.append('skip', params.skip.toString());
            if (params?.limit !== undefined) queryParams.append('limit', params.limit.toString());
            if (params?.include_public !== undefined) queryParams.append('include_public', params.include_public.toString());

            const response = await api.get(`/stations/?${queryParams.toString()}`);

            // Handle paginated response from DRF
            if (response.data && typeof response.data === 'object' && Array.isArray(response.data.results)) {
                return response.data.results; // Return just the results array
            }

            // Handle direct array response (backward compatibility)
            if (Array.isArray(response.data)) {
                return response.data;
            }

            // If neither, return empty array
            return [];
        } catch (error) {
            console.error('Failed to fetch stations from API:', error);
            throw error;
        }
    },

    // GET /api/v1/stations/{station_uuid} - Get station
    getById: async (stationUuid: string) => {
        const response = await api.get(`/stations/${stationUuid}/`);
        return response.data;
    },

    // POST /api/v1/stations/ - Create station
    create: async (data: {
        name: string;
        description?: string;
        location: {
            latitude: number;
            longitude: number;
        };
        elevation?: number;
        timezone?: string;
        source?: string;
        owner?: string;
        public?: boolean;
        status?: string;
        notes?: string;
        tags?: string[];
    }) => {
        const response = await api.post('/stations/', data);
        return response.data;
    },

    // PUT /api/v1/stations/{station_uuid} - Update station
    update: async (stationUuid: string, data: {
        name?: string;
        description?: string;
        location?: {
            latitude: number;
            longitude: number;
        };
        elevation?: number;
        timezone?: string;
        source?: string;
        owner?: string;
        public?: boolean;
        status?: string;
        notes?: string;
        tags?: string[];
        unit_preferences?: Record<string, string>;
    }) => {
        const response = await api.put(`/stations/${stationUuid}/`, data);
        return response.data;
    },

    // DELETE /api/v1/stations/{station_uuid} - Delete station
    delete: async (stationUuid: string) => {
        await api.delete(`/stations/${stationUuid}/`);
    },

    // POST /api/v1/stations/{station_uuid}/regenerate-api-key - Regenerate API key
    regenerateApiKey: async (stationUuid: string) => {
        const response = await api.post(`/stations/${stationUuid}/regenerate-api-key/`);
        return response.data;
    },

    // GET /api/v1/stations/{station_uuid}/timeseries - Get station timeseries
    getTimeseries: async (stationUuid: string, hours: number = 24, limit?: number) => {
        let url = `/stations/${stationUuid}/timeseries/?hours=${hours}`;
        if (limit !== undefined) {
            url += `&limit=${limit}`;
        }
        const response = await api.get(url);
        return response.data;
    },
};

// Measurements service following OpenAPI specification
export const measurementsService = {
    // GET /api/v1/measurements/station/{station_uuid}/latest - Get latest measurements
    getLatest: async (stationUuid: string, limit: number = 1) => {
        const response = await api.get(`/measurements/station/${stationUuid}/latest/?limit=${limit}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/range - Get measurements range
    getRange: async (stationId: number, params?: {
        start_time?: string;
        end_time?: string;
        hours_back?: number;
        measures?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params?.start_time) queryParams.append('start_time', params.start_time);
        if (params?.end_time) queryParams.append('end_time', params.end_time);
        if (params?.hours_back) queryParams.append('hours_back', params.hours_back.toString());
        if (params?.measures) queryParams.append('measures', params.measures);

        const response = await api.get(`/measurements/station/${stationId}/range/?${queryParams.toString()}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/aggregated - Get aggregated measurements
    getAggregated: async (stationId: number, params?: {
        interval?: string;
        hours_back?: number;
        start_time?: string;
        end_time?: string;
    }) => {
        const queryParams = new URLSearchParams();
        if (params?.interval) queryParams.append('interval', params.interval);
        if (params?.hours_back) queryParams.append('hours_back', params.hours_back.toString());
        if (params?.start_time) queryParams.append('start_time', params.start_time);
        if (params?.end_time) queryParams.append('end_time', params.end_time);

        const response = await api.get(`/measurements/station/${stationId}/aggregated/?${queryParams.toString()}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/measures - Get available measures
    getMeasures: async (stationId: number) => {
        const response = await api.get(`/measurements/station/${stationId}/measures/`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/summary - Get summary stats
    getSummary: async (stationId: number, days: number = 7) => {
        const response = await api.get(`/measurements/station/${stationId}/summary/?days=${days}`);
        return response.data;
    },

    // GET /api/v1/measurements/station/{station_id}/hybrid - Get hybrid data
    getHybrid: async (stationId: number) => {
        const response = await api.get(`/measurements/station/${stationId}/hybrid/`);
        return response.data;
    },
};

// Data Ingestion service following OpenAPI specification
export const ingestionService = {
    // POST /api/v1/ingest/{station_api_key} - Ingest HTTP data
    ingestData: async (stationApiKey: string, payload: object, timestamp?: string) => {
        const headers: Record<string, string> = {};
        if (timestamp) {
            headers['X-Timestamp'] = timestamp;
        }

        const response = await api.post(`/ingest/${stationApiKey}/`, payload, { headers });
        return response.data;
    },

    // GET /api/v1/ingest/{station_api_key}/info - Get ingestion info
    getInfo: async (stationApiKey: string) => {
        const response = await api.get(`/ingest/${stationApiKey}/info/`);
        return response.data;
    },

    // POST /api/v1/ingest/sensor/{station_uuid} - Ingest sensor data by UUID
    ingestByUuid: async (stationUuid: string, payload: object, timestamp?: string) => {
        const headers: Record<string, string> = {};
        if (timestamp) {
            headers['X-Timestamp'] = timestamp;
        }

        const response = await api.post(`/ingest/sensor/${stationUuid}/`, payload, { headers });
        return response.data;
    },
};

// Authentication service following OpenAPI specification
export const authService = {
    // POST /api/v1/auth/login - Login for access token
    login: async (credentials: { username: string; password: string }) => {
        // Use form data format for OAuth2 compatibility as per OpenAPI spec
        const formData = new URLSearchParams();
        formData.append('username', credentials.username);
        formData.append('password', credentials.password);

        const response = await api.post('/auth/login/', formData, {
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
        return response.data;
    },

    // Registration removed — authentication is handled by Keycloak.
};

// Dashboard service following OpenAPI specification
export const dashboardService = {
    // GET /api/v1/dashboard/stats - Get dashboard stats
    getStats: async () => {
        const response = await api.get('/dashboard/stats/');
        return response.data;
    },
};

// Radius filter parameters for map
export interface RadiusFilter {
    lat: number;
    lon: number;
    radius: number;  // in kilometers
}

// Map service following OpenAPI specification
export const mapService = {
    // GET /api/v1/map/stations - Get map stations with optional radius filter
    getStations: async (radiusFilter?: RadiusFilter) => {
        let url = '/map/stations/';
        if (radiusFilter) {
            const params = new URLSearchParams({
                lat: radiusFilter.lat.toString(),
                lon: radiusFilter.lon.toString(),
                radius: radiusFilter.radius.toString(),
            });
            url += `?${params.toString()}`;
        }
        const response = await api.get(url);
        return response.data;
    },

    // GET /api/v1/map/stations/bounds - Get stations bounds
    getBounds: async () => {
        const response = await api.get('/map/stations/bounds/');
        return response.data;
    },

    // GET /api/v1/map/stations/{station_id}/status - Get station status
    getStationStatus: async (stationId: string) => {
        const response = await api.get(`/map/stations/${stationId}/status/`);
        return response.data;
    },
};

// Health check service
export const healthService = {
    // GET /health - Health check
    check: async () => {
        const response = await api.get('/health', { baseURL: API_BASE_URL });
        return response.data;
    },

    // GET / - Root endpoint
    root: async () => {
        const response = await api.get('/', { baseURL: API_BASE_URL });
        return response.data;
    },
};

// Legacy compatibility - keeping for backward compatibility with existing code
export const systemHealthService = {
    get: async () => {
        try {
            return await dashboardService.getStats();
        } catch (error) {
            console.error('Failed to fetch system health:', error);
            throw error;
        }
    },
};

// Legacy compatibility - keeping for backward compatibility
export const alertsService = {
    getAll: async () => {
        // TODO: Implement alerts endpoint when available in backend
        return [];
    },
};

// Backward compatibility alias
export const stationTimeseriesService = {
    getTimeseries: stationService.getTimeseries,
    // GET /api/v1/stations/timeseries-batch/ - Get timeseries for multiple stations in one request
    getTimeseriesBatch: async (stationUuids: string[], hours: number = 24): Promise<Record<string, any>> => {
        const response = await api.get(`/stations/timeseries-batch/?station_ids=${stationUuids.join(',')}&hours=${hours}`);
        return response.data;
    },
};

// Station comparison service for multi-station analysis
export const compareService = {
    // GET /api/v1/stations/compare/ - Compare multiple stations
    getComparison: async (params: {
        stationIds: string[];
        metric: string;
        hours?: number;
    }) => {
        const queryParams = new URLSearchParams();
        queryParams.append('station_ids', params.stationIds.join(','));
        queryParams.append('metric', params.metric);
        if (params.hours !== undefined) {
            queryParams.append('hours', params.hours.toString());
        }

        const response = await api.get(`/stations/compare/?${queryParams.toString()}`);
        return response.data;
    },
};

// Type definitions for better type safety
export interface Station {
    uuid: string;
    name: string;
    description?: string;
    location: {
        latitude: number;
        longitude: number;
    };
    public: boolean;
    status: string;
    api_key?: string;
    mqtt_username?: string;
    created_at: string;
    updated_at?: string;
    last_data_received?: string;
    source?: string;
    owner?: string;
    elevation?: number;
    timezone?: string;
}

export interface StationListItem {
    uuid: string;
    name: string;
    location: {
        latitude: number;
        longitude: number;
    };
    public: boolean;
    status: string;
    latest_readings?: Record<string, {
        value: number;
        unit: string;
        timestamp: string;
    }>;
    source?: string;
    owner?: string;
    elevation?: number;
    timezone?: string;
    debug?: boolean;
    created_at?: string;
    updated_at?: string;
}

export interface Token {
    access_token: string;
    token_type: string;
}

// UserRegister interface removed — registration handled by Keycloak

// Alert Rule types
export interface AlertRule {
    id: number;
    station?: number;
    station_uuid?: string;
    station_name?: string;
    name: string;
    metric: string;
    operator: '>' | '<' | '>=' | '<=';
    threshold: number;
    enabled: boolean;
    acknowledged_at: string | null;
    created_at: string;
    updated_at?: string;
}

export interface AlertRuleCreate {
    station_uuid: string;
    name: string;
    metric: string;
    operator: '>' | '<' | '>=' | '<=';
    threshold: number;
    enabled?: boolean;
}

export interface AlertRuleUpdate {
    name?: string;
    metric?: string;
    operator?: '>' | '<' | '>=' | '<=';
    threshold?: number;
    enabled?: boolean;
}

// Triggered alert from backend evaluation
export interface TriggeredAlert {
    rule_id: number;
    rule_name: string;
    station_id?: number;
    station_uuid: string;
    station_name?: string;
    metric: string;
    operator: string;
    threshold: number;
    triggered_value: number;
    triggered_at: string;
    acknowledged_at?: string | null;
}

// Alert Rule service
export const alertRuleService = {
    // GET /api/v1/alert-rules/ - List all user's alert rules
    getAll: async (): Promise<AlertRule[]> => {
        const response = await api.get('/alert-rules/');
        return response.data;
    },

    // GET /api/v1/stations/{station_uuid}/alert-rules/ - Get rules for a station
    getByStation: async (stationUuid: string): Promise<AlertRule[]> => {
        const response = await api.get(`/stations/${stationUuid}/alert-rules/`);
        return response.data;
    },

    // GET /api/v1/alert-rules/{id}/ - Get single rule
    getById: async (id: number): Promise<AlertRule> => {
        const response = await api.get(`/alert-rules/${id}/`);
        return response.data;
    },

    // POST /api/v1/alert-rules/ - Create rule
    create: async (data: AlertRuleCreate): Promise<AlertRule> => {
        const response = await api.post('/alert-rules/', data);
        return response.data;
    },

    // PATCH /api/v1/alert-rules/{id}/ - Update rule
    update: async (id: number, data: AlertRuleUpdate): Promise<AlertRule> => {
        const response = await api.patch(`/alert-rules/${id}/`, data);
        return response.data;
    },

    // DELETE /api/v1/alert-rules/{id}/ - Delete rule
    delete: async (id: number): Promise<void> => {
        await api.delete(`/alert-rules/${id}/`);
    },

    // POST /api/v1/alert-rules/{id}/acknowledge/ - Acknowledge alert
    acknowledge: async (id: number): Promise<{ id: number; acknowledged_at: string }> => {
        const response = await api.post(`/alert-rules/${id}/acknowledge/`);
        return response.data;
    },

    // GET /api/v1/alert-rules/triggered/ - Get all triggered alerts (for Dashboard badges)
    getTriggeredAlerts: async (): Promise<TriggeredAlert[]> => {
        const response = await api.get('/alert-rules/triggered/');
        return response.data;
    },

    // GET /api/v1/stations/{station_uuid}/alerts/triggered/ - Get triggered alerts for a station
    getStationTriggeredAlerts: async (stationUuid: string): Promise<TriggeredAlert[]> => {
        const response = await api.get(`/stations/${stationUuid}/alerts/triggered/`);
        return response.data;
    },
};

// Advanced filtering service - backend-powered timeseries filtering
export const filterService = {
    // POST /api/v1/stations/filter-timeseries/ - Filter timeseries with rules
    filter: async (params: {
        rules: Array<{ metric: string; operator: string; value: number }>;
        match_mode: 'all' | 'any';
        hours?: number;
        limit?: number;
    }): Promise<any> => {
        const response = await api.post('/stations/filter-timeseries/', params);
        return response.data;
    },
};

// Anomaly Detection service - AI-powered anomaly analysis
export const anomalyService = {
    // GET /api/v1/stations/{station_uuid}/anomaly-detection/ - Run anomaly detection
    analyze: async (stationUuid: string): Promise<any> => {
        const response = await api.get(`/stations/${stationUuid}/anomaly-detection/`, {
            timeout: 120000  // 2 minutes - Ollama inference can be slow
        });
        return response.data;
    },
};

// User Groups service
export const groupService = {
    // List my groups (as owner)
    listGroups: async (): Promise<any[]> => {
        const response = await api.get('/groups/');
        return response.data;
    },
    // Create group
    createGroup: async (data: { name: string; description?: string; members_can_add_stations?: boolean }): Promise<any> => {
        const response = await api.post('/groups/', data);
        return response.data;
    },
    // Get group details with members
    getGroup: async (groupId: number): Promise<any> => {
        const response = await api.get(`/groups/${groupId}/`);
        return response.data;
    },
    // Rename group
    renameGroup: async (groupId: number, name: string): Promise<any> => {
        const response = await api.patch(`/groups/${groupId}/`, { name });
        return response.data;
    },
    // Delete group
    deleteGroup: async (groupId: number): Promise<any> => {
        const response = await api.delete(`/groups/${groupId}/`);
        return response.data;
    },
    // Regenerate invite token
    regenerateToken: async (groupId: number): Promise<any> => {
        const response = await api.post(`/groups/${groupId}/regenerate-token/`);
        return response.data;
    },
    // Remove member (auto-regenerates token)
    removeMember: async (groupId: number, userId: number): Promise<any> => {
        const response = await api.delete(`/groups/${groupId}/members/${userId}/remove/`);
        return response.data;
    },
    // Get join page info
    getJoinInfo: async (token: string): Promise<any> => {
        const response = await api.get(`/join/${token}/`);
        return response.data;
    },
    // Accept join
    joinGroup: async (token: string): Promise<any> => {
        const response = await api.post(`/join/${token}/accept/`);
        return response.data;
    },
    // Leave group
    leaveGroup: async (groupId: number): Promise<any> => {
        const response = await api.post(`/groups/${groupId}/leave/`);
        return response.data;
    },
    // List groups I'm a member of
    myMemberships: async (): Promise<any[]> => {
        const response = await api.get('/groups/memberships/');
        return response.data;
    },
    // List stations in a group
    listGroupStations: async (groupId: number): Promise<any[]> => {
        const response = await api.get(`/groups/${groupId}/stations/`);
        return response.data;
    },
    // Add station to group
    addStationToGroup: async (groupId: number, stationUuid: string): Promise<any> => {
        const response = await api.post(`/groups/${groupId}/stations/`, { station_uuid: stationUuid });
        return response.data;
    },
    // Remove station from group
    removeStationFromGroup: async (groupId: number, stationId: number): Promise<any> => {
        const response = await api.delete(`/groups/${groupId}/stations/${stationId}/remove/`);
        return response.data;
    },
    // Stations shared with me
    sharedWithMe: async (): Promise<any[]> => {
        const response = await api.get('/stations/shared-with-me/');
        return response.data;
    },
};

// FTP Config service
export const ftpConfigService = {
    get: async (stationUuid: string): Promise<any> => {
        const response = await api.get(`/stations/${stationUuid}/ftp-config/`);
        return response.data;
    },
    save: async (stationUuid: string, data: any): Promise<any> => {
        const response = await api.put(`/stations/${stationUuid}/ftp-config/`, data);
        return response.data;
    },
    delete: async (stationUuid: string): Promise<any> => {
        const response = await api.delete(`/stations/${stationUuid}/ftp-config/`);
        return response.data;
    },
    test: async (stationUuid: string, data: any): Promise<any> => {
        const response = await api.post(`/stations/${stationUuid}/ftp-config/test/`, data);
        return response.data;
    },
    history: async (stationUuid: string): Promise<any[]> => {
        const response = await api.get(`/stations/${stationUuid}/ftp-config/history/`);
        return response.data;
    },
};

export const validationRulesService = {
    list: async (stationUuid: string): Promise<any[]> => {
        const response = await api.get(`/stations/${stationUuid}/validation-rules/`);
        return response.data;
    },
    create: async (stationUuid: string, data: any): Promise<any> => {
        const response = await api.post(`/stations/${stationUuid}/validation-rules/`, data);
        return response.data;
    },
    update: async (stationUuid: string, ruleId: number, data: any): Promise<any> => {
        const response = await api.put(`/stations/${stationUuid}/validation-rules/${ruleId}/`, data);
        return response.data;
    },
    delete: async (stationUuid: string, ruleId: number): Promise<any> => {
        const response = await api.delete(`/stations/${stationUuid}/validation-rules/${ruleId}/`);
        return response.data;
    },
    log: async (stationUuid: string): Promise<any[]> => {
        const response = await api.get(`/stations/${stationUuid}/validation-log/`);
        return response.data;
    },
    acknowledge: async (stationUuid: string, ruleId: number): Promise<any> => {
        const response = await api.post(`/stations/${stationUuid}/validation-rules/${ruleId}/acknowledge/`);
        return response.data;
    },
    getTriggeredAlerts: async (): Promise<any[]> => {
        const response = await api.get('/validation-rules/triggered/');
        return response.data;
    },
};

export const slackConfigService = {
    get: async (stationUuid: string): Promise<any> => {
        const response = await api.get(`/stations/${stationUuid}/slack-config/`);
        return response.data;
    },
    save: async (stationUuid: string, webhookUrl: string): Promise<any> => {
        const response = await api.put(`/stations/${stationUuid}/slack-config/`, { webhook_url: webhookUrl });
        return response.data;
    },
    test: async (stationUuid: string): Promise<any> => {
        const response = await api.post(`/stations/${stationUuid}/slack-config/test/`);
        return response.data;
    },
    delete: async (stationUuid: string): Promise<any> => {
        const response = await api.delete(`/stations/${stationUuid}/slack-config/`);
        return response.data;
    },
};

export default api;
